/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { HtmlDocumentModifier } from '../../utils/html-document-modifier.js';

/**
 * @prop {String} theme - Default light. Defines the color theme of the page. Accepted values are light and dark.
 * @prop {Boolean} scrollable - Default false. Defines whether the page scrolls or not.
 * @prop {Boolean} flat - Default false. Changes the page background to Base 3 for cases when cards are not necessary for hosting the content.
 * @prop {Boolean} dicom - Default false. Changes the UI2, UI3 and UI4 colors of the page so that the orange shades can be adapted to DICOM compliant monitors.
 * @prop {Boolean} noPadding - Default undefined. Removes the default padding from the main content area of the page.
 * @slot unnamed - Takes any component as a child. Corresponds to the main content area.
 * @slot access - Takes Access Bar as a child.
 * @slot nav - Takes Navigation Bar as a child.
 * @slot left - Takes Pane as a child.
 * @slot right - Takes Pane as a child.
 * @slot footer - Takes Navigation Bar as a child.
 */
export class SHPage extends LitElement {
  static get properties() {
    return {
      theme: { type: String, reflect: true },
      scrollable: { type: Boolean, reflect: true },
      flat: { type: Boolean, reflect: true },
      dicom: { type: Boolean, reflect: true },
      noPadding: { type: Boolean, reflect: true, attribute: 'no-padding' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          height: 100%;
          display: block;
        }

        :host([sh-vrt-mode]) {
          width: initial;
          height: initial;
          display: inline-block;
        }

        :host([sh-vrt-mode]) .page-content {
          /* background-color: initial; */
        }

        .page-wrapper {
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
        }

        .page-content {
          display: flex;
          flex: 1;
          flex-direction: row;
          background-color: var(--base-1);
          transition: var(--transition-time) all ease-in-out;
          overflow: hidden;
        }

        :host([flat]) .page-content {
          background-color: var(--base-3);
        }

        .main-wrapper {
          overflow-y: auto;
          height: 100%;
          width: 100%;
        }

        .body-wrapper {
          flex: 1;
          height: 100%;
          overflow: hidden;
          padding: 1rem;
          box-sizing: border-box;
          position: relative;
          display: flex;
          justify-content: center;
        }
        .body-wrapper[scrollable] {
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          overflow-y: auto;
        }
        :host([no-padding]) .body-wrapper {
          padding: 0;
        }
        .body-wrapper > ::slotted(*) {
          width: 100%;
        }
        .body-wrapper > ::slotted(sh-button),
        .body-wrapper > ::slotted(sh-badge),
        .body-wrapper > ::slotted(sh-tag) {
          width: fit-content;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="page-wrapper">
        <slot name="access" id="access"></slot>
        <slot name="nav" id="nav"></slot>
        <div class="page-content">
          <slot name="left" id="left"></slot>
          <div class="main-wrapper">
            <div class="body-wrapper" ?scrollable="${this.scrollable}">
              <slot></slot>
            </div>
          </div>
          <slot name="right" id="right"></slot>
        </div>
        <slot name="footer" id="footer"></slot>
      </div>
    `;
  }

  constructor() {
    super();
    this.theme = 'light';
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('theme')) {
      this._defineTheme();
    }

    if (changedProperties.has('dicom')) {
      this._dicomObserver();
    }
    const listOfProperties = ['theme', 'scrollable', 'flat', 'dicom', 'noPadding'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  _defineTheme() {
    if (this.theme) {
      HtmlDocumentModifier.setHTMLAttribute('theme', this.theme);
    } else {
      HtmlDocumentModifier.removeHTMLAttribute('theme');
    }
  }

  _dicomObserver() {
    if (this.dicom) {
      HtmlDocumentModifier.setHTMLAttribute('dicom', '');
    } else {
      HtmlDocumentModifier.removeHTMLAttribute('dicom');
    }
  }
}

customElements.define('sh-page', SHPage);
